import { action, observable, runInAction, IObservableArray, makeObservable } from "mobx";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { API } from "General/API";
import { download, fileFromStreamedPayload, StreamedFilePayload } from "utils/download";
import { actionProgressStore } from "Components/ActionProgress/ActionProgressStore";
import { radioButtonValueToSearchPostObject } from "../Components/Search/utils/radioButtonValueToSearchPostObject";
import { searchPostObjectToRadioButtonValue } from "../Components/Search/utils/searchPostObjectToRadioButtonValue";
import { groupBy, sortBy, reverse } from "lodash";
import { getUid } from "utils/uid";
import i18n, { I18N_NS } from "Config/i18n";
import { documentsTableStore } from "./DocumentsTableStore";
import { formatUsername } from "utils/formatUsername";
import { breadcrumbsStore } from "./BreadcrumbsStore";

/**
 * CURR_DIR - search only in current folder
 * CURR_CHILDREN - search in current folder and all it's children
 * ALL - search within all project
 */
export type SearchInRange = "CURR_DIR" | "CURR_CHILDREN" | "ALL";
export type ProblemFilesType = "problematicWithZeroSize" | "problematicWithMalware";
export interface GroupedSearchResult extends API_SearchResult {
  isGroup?: boolean;
  uid?: string;
}

class DocumentsSearchStore extends CoreStore {
  initialSortWasCalled: boolean = false;
  data: IObservableArray<GroupedSearchResult> = [] as IObservableArray<GroupedSearchResult>;
  total: number = 0;
  sortBy: keyof API_SearchResult = null;
  sortDirection: "ASC" | "DESC" = null;
  searchReqQueries: API_DocSearchReq = {};
  radioValue: SearchInRange = "CURR_CHILDREN";
  virusRadioValue: ProblemFilesType = null;
  numberOfMalwareFiles: number = 0;
  exportDownloadState: StoreState = "RAW";

  constructor() {
    super();

    makeObservable(this, {
      initialSortWasCalled: observable,
      data: observable,
      total: observable,
      sortBy: observable,
      sortDirection: observable,
      searchReqQueries: observable,
      radioValue: observable,
      numberOfMalwareFiles: observable,
      exportDownloadState: observable,
      clear: action.bound,
      setRadioValue: action.bound,
      setVirusRadioValue: action.bound,
      preparePostObject: action.bound,
      setRadioButtonFromSearchQuery: action.bound,
      submitSearch: action.bound,
      setSearchReqQuery: action.bound,
      downloadExcel: action.bound,
      getPostObject: action.bound,
      refresh: action.bound,
      sort: action.bound,
      sendRequest: action.bound,
    });
  }

  url() {
    return API_URL.SEARCH.getSearchResults(projectsDataStore.projectId);
  }

  clear() {
    this.data = [] as IObservableArray<API_SearchResult>;
    this.total = 0;
    this.searchReqQueries = {};
    this.radioValue = "CURR_CHILDREN";
    this.virusRadioValue = null;
    this.sortBy = null;
    this.sortDirection = null;
    /* In Classic initial search results are grouped by folder, but when sorting is called - grouping gets removed.
    In order to handle this scenario we use this flag to drop call of sorting function after fetch request is made. 
    This approach relies on ScrollableTable's ComponentDidUpdate method 
    */
    this.initialSortWasCalled = false;
    this.numberOfMalwareFiles = 0;
    this.exportDownloadState = "RAW";
  }

  setRadioValue(newValue: SearchInRange) {
    this.radioValue = newValue;
  }

  setVirusRadioValue(newValue: ProblemFilesType) {
    this.virusRadioValue = newValue;
  }

  preparePostObject(postObject: API_DocSearchReq): API_DocSearchReq {
    return radioButtonValueToSearchPostObject(postObject, this.radioValue, this.virusRadioValue);
  }

  setRadioButtonFromSearchQuery(postObject: API_DocSearchReq) {
    const newValue = searchPostObjectToRadioButtonValue(postObject);
    this.radioValue = newValue;
  }

  submitSearch(formData: API_DocSearchReq) {
    const postObject = this.preparePostObject(formData);
    return this.sendRequest(postObject);
  }

  preparePostObjectFromFormData(formData: API_DocSearchReq) {
    const radioValue = searchPostObjectToRadioButtonValue(formData);
    if (!formData.folderId) {
      formData.folderId = documentsTableStore.currentFolderId;
    }
    return radioButtonValueToSearchPostObject(formData, radioValue, this.virusRadioValue);
  }

  refresh() {
    const postObject = this.preparePostObject({
      returnDeleted: documentsTableStore.showDeleted,
      ...this.searchReqQueries,
    });
    return this.sendRequest(postObject);
  }

  @handleStoreState()
  async sendRequest(formData: API_DocSearchReq) {
    const payload = this.preparePostObjectFromFormData(formData);
    this.setSearchReqQuery(payload);
    try {
      this.initialSortWasCalled = false;
      const response: API_SearchResult[] = await API.newData(this.url(), payload);
      const withUniqueIds = response.map((item) => ({
        ...item,
        // uid is needed to distinguish between files with state === 'SYMLINK'
        // and others since they share same id property
        uid: getUid(),
        logicalPath: item.parentId === 0 ? (i18n.t("Table view", { ns: I18N_NS.PROJECT }) as string) : item.logicalPath,
      }));
      runInAction(() => {
        if (formData.docText) {
          this.data.replace(withUniqueIds);
        } else {
          const projectName = projectsDataStore.currentProject.name;
          const groupedByFolder = groupBy(withUniqueIds, "logicalPath");
          const groupedByFolderArray = Object.keys(groupedByFolder).reduce((array, objKey) => {
            const parentId = groupedByFolder[objKey][0].parentId;
            const groupLabel: GroupedSearchResult = {
              isGroup: true,
              name: `${projectName}/${objKey}`,
              properties: {},
              fileActions: {},
              id: parentId,
              uid: parentId.toString(),
            };
            array.push(groupLabel, ...groupedByFolder[objKey]);
            return array;
          }, []);

          this.data.replace(groupedByFolderArray);
        }
        this.total = response.length;
        this.numberOfMalwareFiles = response.filter((item) => item.malware).length;
        if (formData.returnDeleted !== documentsTableStore.showDeleted) {
          this.toggleShowDeleted(false);
        }
        if (formData.folderId) {
          breadcrumbsStore.init(formData.folderId);
        }
        this.setVirusRadioValue(null);
        this.setRadioButtonFromSearchQuery(formData);
      });
    } catch (err) {
      throw err;
    }
  }

  setSearchReqQuery(obj: API_SearchQuery) {
    this.searchReqQueries = JSON.parse(JSON.stringify(obj));
    this.radioValue = searchPostObjectToRadioButtonValue(obj);
  }

  getFilesFromIds(fileIds: number[]) {
    const files: API_SearchResult[] = fileIds.reduce((arr, item) => {
      const foundItem = this.data.find((file) => file.id === item);
      if (foundItem) {
        arr.push(foundItem);
      } else {
        console.warn(`FILE ID "${item}" NOT FOUND`);
      }
      return arr;
    }, []);
    return files;
  }

  async getAllFileOwnerOptions() {
    const options: API_User[] = await API.getData(API_URL.FILES.getAllFileOwners(projectsDataStore.projectId));
    return options.map((item) => ({
      value: item.id,
      label: formatUsername({ firstName: item.firstName, surname: item.surname }),
    }));
  }

  @handleStoreState<DocumentsSearchStore>("exportDownloadState")
  async downloadExcel(filesToSubmit: API_FileDataEntity[]) {
    try {
      const files: API_MultipleFileRequest["files"] = filesToSubmit.map((file) => {
        if (file.state === "SYMLINK") {
          return { id: file.linkId, state: "SYMLINK" };
        } else if (file.state === "EXTERN_SYMLINK") {
          return { id: file.id, linkId: file.linkId, state: "EXTERN_SYMLINK" };
        } else {
          return { id: file.id, state: file.state };
        }
      });
      const formBody: API_MultipleFileRequest = { files };
      const progressId = actionProgressStore.init();
      try {
        const payload = await API.newData<StreamedFilePayload>(
          API_URL.FILES.getExcelFileList(projectsDataStore.projectId),
          formBody,
          null,
          {
            cancelToken: actionProgressStore.getCancelToken(progressId),
            onProgress: (percent) => actionProgressStore.setPercent(progressId, percent),
          }
        );
        const excel = fileFromStreamedPayload(payload);
        download(excel, excel.name, excel.type);
      } finally {
        actionProgressStore.dismiss(progressId);
      }
    } catch (error) {
      throw error;
    }
  }

  getAllStatusOfFilesInProject() {
    return API.getData(API_URL.SEARCH.getAllStatusOfFilesInProject(projectsDataStore.projectId));
  }

  getPostObject() {
    return this.preparePostObject(this.searchReqQueries);
  }

  sort = (sortByKey: keyof API_SearchResult, sortDirection: "ASC" | "DESC") => {
    if (!this.initialSortWasCalled) {
      this.initialSortWasCalled = true;
      return;
    }
    if (sortByKey === this.sortBy && sortDirection === this.sortDirection) {
      return;
    }
    const sorted = sortBy(
      this.data.filter((item) => !item.isGroup),
      sortByKey
    );
    if (sortDirection === "ASC") {
      this.data.replace(sorted);
    } else {
      this.data.replace(reverse(sorted));
    }
    if (sortByKey) {
      this.sortBy = sortByKey;
    }
    if (sortDirection) {
      this.sortDirection = sortDirection;
    }
  };

  async toggleShowDeleted(refresh: boolean = true) {
    await documentsTableStore.toggleShowDeleted(false);
    runInAction(() => {
      this.searchReqQueries.returnDeleted = documentsTableStore.showDeleted;
    });
    if (refresh) {
      await this.refresh();
    }
  }
}

export const documentsSearchStore = new DocumentsSearchStore();
